<template>
  <div v-if="isVisible && isReady" class="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md relative">
      <div
        v-if="isGenerating"
        class="absolute inset-0 bg-white/60 dark:bg-gray-900/60 flex items-center justify-center rounded-lg z-10"
      >
        <span class="text-gray-700 dark:text-gray-200 text-sm">Generating closing notes…</span>
      </div>

      <h2 class="text-lg font-semibold dark:text-gray-100 mb-4">Complete Ticket: #{{ sourceTicketId }}</h2>

      <div v-if="errorMsg" class="mb-4 p-2 text-xs rounded bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200">
        {{ errorMsg }}
      </div>
      <div
        v-if="!requesterPhone"
        class="mb-4 p-2 rounded border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-500"
      >
        <p class="text-xs text-yellow-900 dark:text-yellow-100">
          Reminder: the requester has no phone number on file. If you can, ask them for one and add it to the ticket before closing.
        </p>
      </div>
      <div
        v-if="requesterName || requesterEmail"
        class="mb-4"
      >
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">
          Requester
        </label>

        <div
          class="w-full p-2 rounded-md border border-gray-300 shadow-sm text-xs dark:bg-gray-500 dark:border-gray-600 dark:text-gray-100"
        >
          <span class="font-semibold">
            {{ requesterName || 'Unknown' }}
          </span>
          <a
            v-if="requesterEmail"
            :href="`mailto:${requesterEmail}`"
            class="ml-1 text-xs underline underline-offset-2 hover:opacity-80"
            title="Email requester"
          >
            ({{ requesterEmail }})
          </a>
        </div>
      </div>
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Completed Date</label>
        <input
          type="datetime-local"
          v-model="day"
          step="1"
          class="w-full p-2 rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-500 dark:border-gray-600 dark:text-gray-100"
        >
      </div>

      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Ticket Type</label>
        <select
          v-model="selectedTicketTypeId"
          class="w-full p-2 rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-500 dark:border-gray-600 dark:text-gray-100"
        >
          <option v-for="type in allTypes" :key="type.id" :value="type.id">{{ type.description }}</option>
        </select>
      </div>

      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Completed By</label>
        <select
          v-model="selectedTech"
          class="w-full p-2 rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-500 dark:border-gray-600 dark:text-gray-100"
        >
          <template v-for="(techList, deptName) in groupedTechs" :key="deptName">
            <optgroup v-if="deptName" :label="deptName">
              <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                {{ tech.name }}
              </option>
            </optgroup>
            <template v-else>
              <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                {{ tech.name }}
              </option>
            </template>
          </template>
        </select>
      </div>

      <div class="mb-2 flex items-center justify-between">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Closing Note</label>
        <button
          type="button"
          class="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
          :disabled="isGenerating"
          @click="regenerate()"
        >
          Generate Detailed Notes
        </button>
      </div>
      <div class="mb-4">
        <textarea
          v-model="closingNote"
          rows="6"
          class="w-full p-2 rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-600 dark:text-gray-100"
        ></textarea>
      </div>

      <div class="flex items-center mb-4" v-if="ticketObj && (ticketObj.internal == 0) && (ticketObj.unpublished == 0)">
        <input type="checkbox" v-model="sendEmail" class="mr-2 text-blue-600 dark:text-gray-900 rounded">
        <label for="sendEmail" class="text-sm font-medium text-gray-700 dark:text-gray-100">Send email notification</label>
      </div>

      <!-- Heads-up when this ticket is tied to an active broadcast -->
      <div v-if="willEndBroadcast" class="mb-4 p-2 rounded border border-red-400 bg-red-50 dark:bg-red-900/30 dark:border-red-500">
        <p class="text-xs text-red-800 dark:text-red-100">
          Completing this ticket will also <strong>end the active broadcast</strong><span v-if="broadcastSubject"> “{{ broadcastSubject }}”</span> and take its banner down.
        </p>
      </div>

      <div class="flex justify-end space-x-2 mt-4">
        <button @click="closeModal()" class="bg-gray-400 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded dark:bg-red-500 dark:hover:bg-red-700">
          Cancel
        </button>
        <button
          @click="save()"
          class="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded dark:bg-yellow-500 dark:hover:bg-yellow-700"
          :disabled="isGenerating"
        >
          Mark Complete
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    isVisible: { type: Boolean, required: true },
    sourceTicketId: { type: Number, required: true },
    lastActivity: { type: Date, required: true },
    techs: { type: Array, required: true },
    allTypes: { type: Array, required: true },
    currentTech: { type: Number, required: true },
    currentTicketTypeId: { type: Number, required: true },
    emailnotification: { type: Number, required: true },
    requesterName: { type: String, default: '' },
    requesterEmail: { type: String, default: '' },
    requesterPhone: { type: String, default: '' },
    ticketObj: { type: Object, default: null },
  },
  data() {
    const d = this.lastActivity ? new Date(this.lastActivity) : new Date();
    return {
      selectedTech: this.currentTech,
      selectedTicketTypeId: this.currentTicketTypeId,
      day: this.formatForDatetimeLocal(d),
      sendEmail: this.emailnotification == '1',
      closingNote: '',
      isGenerating: false,
      isReady: false,
      errorMsg: '',
      lastAiText: '',
      lastAiMode: 'brief',
      willEndBroadcast: false,
      broadcastSubject: '',
    };
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        const d = this.lastActivity ? new Date(this.lastActivity) : new Date();
        this.selectedTech = this.currentTech;
        this.selectedTicketTypeId = this.currentTicketTypeId;
        this.day = this.formatForDatetimeLocal(d);
        this.sendEmail = this.emailnotification == '1';
        this.closingNote = '';
        this.errorMsg = '';
        this.generateNotes('brief');
        this.checkBroadcast();
      } else {
        this.isReady = false;
        this.isGenerating = false;
        this.errorMsg = '';
        this.willEndBroadcast = false;
        this.broadcastSubject = '';
      }
    },
    sourceTicketId(newVal, oldVal) {
      if (this.isVisible && newVal && newVal !== oldVal) {
        this.isReady = false;
        this.generateNotes('brief');
        this.checkBroadcast();
      }
    }
  },
  computed: {
    groupedTechs() {
      const groups = {};
      const ungrouped = [];
      this.techs.forEach(tech => {
        if (!tech.deptId) {
          ungrouped.push({ id: tech.id, name: tech.name });
          return;
        }
        let groupKey = '';
        if (tech.deptId === 'External Tech') groupKey = 'External Tech';
        else if (tech.details) groupKey = tech.details;
        else groupKey = 'Other Department';

        if (!groups[groupKey]) groups[groupKey] = [];
        let displayName = tech.name;
        if (tech.deptId === 'External Tech' && tech.details) {
          displayName += ` (${tech.details})`;
        }
        groups[groupKey].push({ id: tech.id, name: displayName });
      });

      if (ungrouped.length > 0) groups[''] = ungrouped;

      let keys = Object.keys(groups)
        .filter(k => k !== 'External Tech')
        .sort((a, b) => {
          if (a === '') return -1;
          if (b === '') return 1;
          return a.localeCompare(b);
        });

      if (groups['External Tech']) keys.push('External Tech');

      const ordered = {};
      keys.forEach(k => (ordered[k] = groups[k]));
      return ordered;
    }
  },
  mounted() {
    this.selectedTech = this.currentTech;
    this.selectedTicketTypeId = this.currentTicketTypeId;
  },
  methods: {
    async generateNotes(mode) {
      this.$updateLoading(1);
      this.isGenerating = true;
      this.errorMsg = '';
      try {
        const { data } = await axios.get(`ticket/closingnotes/${this.sourceTicketId}`, {
          params: { mode }
        });
        this.closingNote = (data?.closing_notes || '').trim();
        this.lastAiText  = this.closingNote;
        this.lastAiMode  = mode;
        if (!this.closingNote) {
          this.errorMsg = 'AI returned an empty response. You can type manually or click Regenerate.';
        }
        this.isReady = true;
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || 'Failed to generate closing notes. You can type manually or click Regenerate.';
        this.closingNote = '';
        this.isReady = true;
      } finally {
        this.isGenerating = false;
        this.$updateLoading(-1);
      }
    },
    regenerate() {
      this.generateNotes('detailed');
    },
    // Is this ticket tied to an active broadcast? If so, warn the tech that
    // closing will end it, and default the "notify whole company" choice to the
    // broadcast's own setting. Uses the shared active-broadcasts feed (internal
    // callers see all live broadcasts; extTech see their own client's).
    async checkBroadcast() {
      this.willEndBroadcast = false;
      this.broadcastSubject = '';
      try {
        const { data } = await axios.get('broadcasts/active');
        const list = Array.isArray(data) ? data : (data.data || []);
        const match = list.find(b => Number(b.ticket_id) === Number(this.sourceTicketId));
        if (match) {
          this.willEndBroadcast = true;
          this.broadcastSubject = match.subject || '';
        }
      } catch (e) {
        // Non-fatal — the ticket still closes; the broadcast is ended server-side.
      }
    },
    closeModal() {
      this.selectedTech = this.currentTech;
      this.selectedTicketTypeId = this.currentTicketTypeId;
      this.day = this.formatForDatetimeLocal(this.lastActivity ? new Date(this.lastActivity) : new Date());
      this.sendEmail = this.emailnotification == '1';
      this.closingNote = '';
      this.isReady = false;
      this.isGenerating = false;
      this.errorMsg = '';
      this.$emit('close');
    },
    formatForDatetimeLocal(d) {
      if (!(d instanceof Date) || isNaN(d.getTime())) d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
    },
    save() {
      if (this.selectedTech && this.day && this.closingNote && this.selectedTicketTypeId) {
        const aiEdited = this.closingNote.trim() !== (this.lastAiText || '').trim();
        const payload = {
          ticketId: this.sourceTicketId,
          techId: this.selectedTech,
          day: this.day,
          sendEmail: this.sendEmail ? 1 : 0,
          closingNote: this.closingNote,
          ticketTypeId: this.selectedTicketTypeId,
          aiEdited: aiEdited ? 1 : 0
        };
        this.$emit('complete', payload);
        this.closeModal();
      } else {
        alert('Please fill in all fields.');
      }
    }
  }
};
</script>

<style>
.dark-mode .scrollable-requester-list {
  background-color: #2d3748;
  color: #a0aec0;
}
.scrollable-requester-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #ccc;
}
</style>
